import { useLayoutEffect, useRef, useState } from 'react'
import { prefersReducedMotion } from './canvasMotion'

export const REFLOW_MS = 240
export interface Bounds { x: number; y: number; width: number; height: number }
interface FlowBox {
  id: string
  position: { x: number; y: number }
  width?: number
  height?: number
  dragging?: boolean
  data: Record<string, any>
}

export function interpolateBounds(from: Bounds, to: Bounds, progress: number): Bounds {
  const t = Math.max(0, Math.min(1, progress))
  return {
    x: from.x + (to.x - from.x) * t,
    y: from.y + (to.y - from.y) * t,
    width: from.width + (to.width - from.width) * t,
    height: from.height + (to.height - from.height) * t,
  }
}

/** Presentation only. Never feed these coordinates to history, saves or drop resolution.
 * One clock controls all bounds; metadata/selection changes don't restart it.
 */
export function useResizeReflow<T extends FlowBox>(nodes: T[], identity: number): T[] {
  const target = new Map(nodes.map(n => [n.id, {
    ...n.position, width: n.width ?? 260, height: n.height ?? 0,
  }]))
  const signature = JSON.stringify([...target])
  const dragging = nodes.some(n => n.dragging)
  const current = useRef(new Map<string, Bounds>())
  const previousIdentity = useRef(identity)
  const [drawn, setDrawn] = useState(current.current)
  const targetRef = useRef(target)
  targetRef.current = target

  useLayoutEffect(() => {
    const goal = targetRef.current
    const from = current.current
    let frame = 0
    const publish = (value: Map<string, Bounds>) => {
      current.current = value
      setDrawn(value)
    }
    const first = from.size === 0 || previousIdentity.current !== identity
    previousIdentity.current = identity
    if (first || dragging || prefersReducedMotion()) {
      publish(goal)
      return
    }
    let started: number | undefined
    const tick = (now: number) => {
      started ??= now
      const progress = prefersReducedMotion() ? 1 : Math.min(1, (now - started) / REFLOW_MS)
      const eased = 1 - Math.pow(1 - progress, 3)
      publish(progress === 1 ? goal : new Map([...goal].map(([id, bounds]) =>
        [id, interpolateBounds(from.get(id) ?? bounds, bounds, eased)],
      )))
      if (progress < 1) frame = requestAnimationFrame(tick)
    }
    frame = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(frame)
  }, [signature, dragging, identity])

  return nodes.map(node => {
    const bounds = dragging ? target.get(node.id)! : drawn.get(node.id) ?? target.get(node.id)!
    return {
      ...node,
      position: { x: bounds.x, y: bounds.y },
      width: bounds.width,
      height: bounds.height,
      style: { ...(node as any).style, width: bounds.width, height: bounds.height },
      data: { ...node.data, displayedHeight: bounds.height },
    }
  })
}

/** Route from the very same bounds as the cards, not ResizeObserver's previous
 * frame of handle measurements. Siblings share one branching level.
 */
export function withDisplayedRoutes<T extends { source: string; target: string; data?: any }>(
  edges: T[], nodes: FlowBox[],
): T[] {
  const byId = new Map(nodes.map(n => [n.id, n]))
  const childTop = new Map<string, number>()
  for (const edge of edges) {
    const child = byId.get(edge.target)
    if (child) childTop.set(edge.source, Math.min(childTop.get(edge.source) ?? Infinity, child.position.y))
  }
  return edges.map(edge => {
    const source = byId.get(edge.source)
    const target = byId.get(edge.target)
    if (!source || !target) return edge
    const sourceY = source.position.y + (source.height ?? 0)
    return { ...edge, data: { ...edge.data, bounds: {
      sourceX: source.position.x + (source.width ?? 260) / 2,
      sourceY,
      targetX: target.position.x + (target.width ?? 260) / 2,
      targetY: target.position.y,
      centerY: (sourceY + childTop.get(edge.source)!) / 2,
    } } }
  })
}
